import { useEffect, useState } from 'react'
import QRCodeGenerator from 'qrcode'
import styled from '@emotion/styled'
import Box from './Box'

type Props = {
  value: string
  showText?: boolean
  size?: number
}

/**
 * Render QR as a PNG via `qrcode`, never `react-qr-code`.
 *
 * `react-qr-code` is CJS (`__esModule` + `exports.default`) and depends on `qr.js@0.0.0`.
 * Under Rolldown — Vite 8's bundler, in both `vite build` and the dev optimizer — the
 * interop hands back the module namespace `{ QRCode, default }` instead of the component,
 * so rendering it throws React error #130 and the QR never appears. That took down admin-web's
 * "Show QR" in production on 2026-08-22. user-web is still on Vite 6 and so was unaffected,
 * but the same import would break the moment it bumps; this removes the trap ahead of time.
 *
 * See kb/ops/vite-8-rolldown-cjs-default-interop.md.
 */
const QRCode_ERROR_CORRECTION_LEVEL = 'L'

const QRCodeElement = ({ value, showText, size = 256 }: Props) => {
  const [pngDataUrl, setPngDataUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (value === '') {
      setPngDataUrl(null)
      setFailed(false)
      return
    }

    let isCancelled = false
    setFailed(false)

    QRCodeGenerator.toDataURL(value, {
      errorCorrectionLevel: QRCode_ERROR_CORRECTION_LEVEL,
      margin: 0,
      width: size,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    })
      .then((dataUrl) => {
        if (!isCancelled) {
          setPngDataUrl(dataUrl)
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setPngDataUrl(null)
          setFailed(true)
        }
      })

    return () => {
      isCancelled = true
    }
  }, [size, value])

  /**
   * `react-qr-code` rendered synchronously on first paint; generating a PNG does not.
   * Without this the wrapper collapses to its padding and then jumps to `size` when the
   * data URL lands — a reflow on every screen that shows a QR, and one per card on the
   * drop-in ticket list. Reserve the box for the gap instead.
   */
  const isPending = value !== '' && pngDataUrl === null && !failed

  return (
    <Box fullWidth direction="column" justify="space-between" align="center">
      <QRWrapper>
        {pngDataUrl !== null ? (
          <QRCodeImage src={pngDataUrl} alt="QR code" width={size} height={size} />
        ) : failed ? (
          <QRFallback>{value}</QRFallback>
        ) : isPending ? (
          <QRPlaceholder style={{ width: size, height: size }} aria-hidden />
        ) : null}
      </QRWrapper>
      {showText && <span>{value}</span>}
    </Box>
  )
}

/**
 * QR is always rendered as black on white. These styles apply unconditionally so
 * browsers that auto-darken pages (Samsung Internet, forced-colors, etc.) do not
 * thin or recolor the image — not only when `prefers-color-scheme: dark` matches.
 */
const QRWrapper = styled.div`
  background: #ffffff;
  padding: 20px;
  border-radius: 8px;
  display: inline-block;
  color-scheme: light only;
  isolation: isolate;
  forced-color-adjust: none;
  border: 2px solid #c8c8c8;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.35);

  & img {
    forced-color-adjust: none;
  }
`

const QRCodeImage = styled.img`
  display: block;
`

const QRPlaceholder = styled.div`
  display: block;
  background: #ffffff;
`

const QRFallback = styled.span`
  display: block;
  max-width: 256px;
  word-break: break-all;
  color: #111111;
  font-size: 0.85rem;
`

export default QRCodeElement
